import { StopRecord } from '../types';
import { supabase } from '../lib/supabaseClient';

/**
 * Convierte una cadena Base64 (DataURL) a un objeto Blob binario para su subida.
 */
function dataURLtoBlob(dataurl: string): Blob {
  try {
    const arr = dataurl.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  } catch (e) {
    console.error('Error al convertir DataURL a Blob', e);
    throw new Error('Formato de imagen Base64 no válido');
  }
}

/**
 * Sube una foto a Supabase Storage y retorna su URL pública.
 * Soporta archivos File/Blob y strings Base64.
 * Si falla, retorna la fuente original (ej. la string Base64) como fallback.
 */
export const uploadPhoto = async (photoSource: File | Blob | string, prefix: string): Promise<string> => {
  let fileToUpload: File | Blob;
  let fileType = 'image/jpeg';

  if (typeof photoSource === 'string') {
    if (photoSource.startsWith('data:image/')) {
      try {
        fileToUpload = dataURLtoBlob(photoSource);
        fileType = fileToUpload.type;
      } catch (err) {
        console.warn('Fallo al parsear Base64, se guardará como string de texto:', err);
        return photoSource; // Guardar la cadena Base64 original si falla el parseo
      }
    } else {
      // Ya es una URL web o un recurso público, no hace falta subirla
      return photoSource;
    }
  } else {
    fileToUpload = photoSource;
    fileType = photoSource.type;
  }

  try {
    const fileExt = fileType.split('/')[1] || 'jpg';
    const fileName = `${prefix}_${Date.now()}_${Math.random().toString(36).substring(2, 7)}.${fileExt}`;

    const { error } = await supabase.storage
      .from('stop-photos')
      .upload(fileName, fileToUpload, {
        contentType: fileType,
        cacheControl: '3600',
        upsert: true
      });

    if (error) {
      throw error;
    }

    const { data: { publicUrl } } = supabase.storage
      .from('stop-photos')
      .getPublicUrl(fileName);

    return publicUrl;
  } catch (storageError) {
    console.error('Error en Supabase Storage (se usará el fallback de Base64/Texto):', storageError);
    // Si la subida falla (ej. si el bucket no existe en el Supabase local del cliente),
    // devolvemos el recurso original para no tumbar el flujo de la aplicación.
    return typeof photoSource === 'string' ? photoSource : ''; 
  }
};

export const stopRecordService = {
  /**
   * Obtiene los registros históricos limitados a 1500 registros ordenados por tiempo de inicio.
   */
  async fetchStopRecords(): Promise<StopRecord[]> {
    const { data, error } = await supabase
      .from('stop_records')
      .select('*')
      .order('start_time', { ascending: false })
      .limit(1500);

    if (error) throw error;

    const rawRecords = data || [];
    return rawRecords.map((r: any) => ({
      id: r.id,
      meterId: r.meter_id,
      startTime: new Date(r.start_time).getTime(),
      endTime: r.end_time ? new Date(r.end_time).getTime() : undefined,
      startValue: r.start_value,
      endValue: r.end_value ?? undefined,
      reason: r.reason,
      supervisorName: r.supervisor_name ?? undefined,
      startPhoto: r.start_photo ?? undefined,
      endPhoto: r.end_photo ?? undefined,
      isManualStart: r.is_manual_start,
      isManualEnd: r.is_manual_end ?? undefined
    }));
  },

  /**
   * Inicia el registro de una parada de medidor. Sube la foto si existe.
   */
  async startStopRecord(
    stopData: Omit<StopRecord, 'id' | 'startTime'> & { meterId: string },
    photoFile?: File | null
  ): Promise<StopRecord> {
    const newRecordDate = new Date();
    let finalPhotoUrl = stopData.startPhoto || '';

    // Intentar subir imagen si existe un archivo físico o si viene en Base64
    const photoSource = photoFile || stopData.startPhoto;
    if (photoSource) {
      finalPhotoUrl = await uploadPhoto(photoSource, `start_${stopData.meterId}`);
    }

    const { data, error } = await supabase
      .from('stop_records')
      .insert({
        meter_id: stopData.meterId,
        start_time: newRecordDate.toISOString(),
        start_value: stopData.startValue,
        reason: stopData.reason,
        supervisor_name: stopData.supervisorName,
        start_photo: finalPhotoUrl || null,
        is_manual_start: stopData.isManualStart,
        is_manual_end: stopData.isManualEnd
      })
      .select()
      .single();

    if (error) {
      console.error('Error insertando registro de parada:', error);
      throw error;
    }

    return {
      id: data.id,
      meterId: data.meter_id,
      startTime: newRecordDate.getTime(),
      endTime: undefined,
      startValue: data.start_value,
      endValue: undefined,
      reason: data.reason,
      supervisorName: data.supervisor_name,
      startPhoto: data.start_photo || undefined,
      endPhoto: undefined,
      isManualStart: data.is_manual_start,
      isManualEnd: data.is_manual_end
    };
  },

  /**
   * Finaliza una parada de medidor. Sube la foto si existe.
   */
  async endStopRecord(stopData: {
    recordId: string;
    endValue: number;
    endPhoto?: string;
    endPhotoFile?: File | null;
    isManualEnd: boolean;
  }): Promise<{ endTime: number; endValue: number; endPhoto?: string; isManualEnd: boolean }> {
    const endDate = new Date();
    let finalPhotoUrl = stopData.endPhoto || '';

    const photoSource = stopData.endPhotoFile || stopData.endPhoto;
    if (photoSource) {
      finalPhotoUrl = await uploadPhoto(photoSource, `end_${stopData.recordId}`);
    }

    const { error } = await supabase
      .from('stop_records')
      .update({
        end_time: endDate.toISOString(),
        end_value: stopData.endValue,
        end_photo: finalPhotoUrl || null,
        is_manual_end: stopData.isManualEnd
      })
      .eq('id', stopData.recordId);

    if (error) {
      console.error('Error finalizando registro de parada:', error);
      throw error;
    }

    return {
      endTime: endDate.getTime(),
      endValue: stopData.endValue,
      endPhoto: finalPhotoUrl || undefined,
      isManualEnd: stopData.isManualEnd
    };
  },

  /**
   * Actualiza los datos de un registro de parada histórico.
   */
  async updateStopRecord(updated: StopRecord): Promise<void> {
    // Si la foto es nueva Base64, subirla antes de guardar en la DB
    let startPhotoUrl = updated.startPhoto || '';
    if (updated.startPhoto && updated.startPhoto.startsWith('data:image/')) {
      startPhotoUrl = await uploadPhoto(updated.startPhoto, `start_${updated.meterId}`);
    }

    let endPhotoUrl = updated.endPhoto || '';
    if (updated.endPhoto && updated.endPhoto.startsWith('data:image/')) {
      endPhotoUrl = await uploadPhoto(updated.endPhoto, `end_${updated.id}`);
    }

    const { error } = await supabase
      .from('stop_records')
      .update({
        start_time: new Date(updated.startTime).toISOString(),
        end_time: updated.endTime ? new Date(updated.endTime).toISOString() : null,
        start_value: updated.startValue,
        end_value: updated.endValue,
        reason: updated.reason,
        supervisor_name: updated.supervisorName,
        start_photo: startPhotoUrl || null,
        end_photo: endPhotoUrl || null,
        is_manual_start: updated.isManualStart,
        is_manual_end: updated.isManualEnd,
        meter_id: updated.meterId
      })
      .eq('id', updated.id);

    if (error) {
      console.error('Error actualizando registro histórico:', error);
      throw error;
    }
  }
};
